import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { playbookKeys } from '../api/queries/PlaybookQueries'

interface TeamContextType {
	currentTeamId: string | null
	setCurrentTeamId: (teamId: string | null) => void
}

const TeamContext = createContext<TeamContextType | undefined>(undefined)

const STORAGE_KEY = 'currentTeamId'

function getStoredTeamId(): string | null {
	if (typeof window === 'undefined' || typeof localStorage === 'undefined') return null
	try {
		const stored = localStorage.getItem(STORAGE_KEY)
		return stored || null
	} catch {
		return null
	}
}

function storeTeamId(teamId: string | null): void {
	if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
		try {
			if (teamId === null) {
				localStorage.removeItem(STORAGE_KEY)
			} else {
				localStorage.setItem(STORAGE_KEY, teamId)
			}
		} catch {
			// Silently fail if localStorage is not available
		}
	}
}

export function TeamProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient()
	const [currentTeamId, setCurrentTeamIdState] = useState<string | null>(() =>
		getStoredTeamId(),
	)

	const setCurrentTeamId = useCallback((teamId: string | null) => {
		setCurrentTeamIdState(teamId)
		storeTeamId(teamId)

		// Invalidate content caches when team switches to force refetch with new teamId
		queryClient.invalidateQueries({ queryKey: playbookKeys.lists() })
	}, [queryClient])

	return (
		<TeamContext.Provider value={{ currentTeamId, setCurrentTeamId }}>
			{children}
		</TeamContext.Provider>
	)
}

export function useTeamContext() {
	const context = useContext(TeamContext)
	if (context === undefined) {
		throw new Error('useTeamContext must be used within a TeamProvider')
	}
	return context
}
